import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs, updateDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        // Buscar dados adicionais do Firestore
        try {
          const userDocRef = doc(db, 'usuarios', authUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            // Combinar dados do Auth com dados do Firestore
            setUser({
              ...authUser,
              role: userData.role || 'user',
              nome: userData.nome || userData.nomeExibicao || authUser.displayName || '',
              nomeExibicao: userData.nomeExibicao || userData.nome || authUser.displayName || '',
              telefone: userData.telefone,
              status: userData.status
            });
          } else {
            // Se não encontrar no Firestore, usar só dados do Auth
            setUser({
              ...authUser,
              role: 'user'
            });
          }
        } catch (error) {
          setUser({
            ...authUser,
            role: 'user'
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      // Primeiro, tentar login normal
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      // Erro no login
      // Se falhar, verificar se é um usuário pendente
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        try {
          // Buscar usuário pendente no Firestore
          const q = query(
            collection(db, 'usuarios'),
            where('email', '==', email)
          );

          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const pendingUser = querySnapshot.docs[0];
            const userData = pendingUser.data();

            // Verificar se é um usuário pendente
            if (userData.precisaPrimeiroLogin === true || userData.precisaPrimeiroLogin === 'true' || userData.precisaPrimeiroLogin === 1) {
              // Verificar se a senha está correta
              if (userData.password === password) {
                // Criar conta no Firebase Auth
                const newAuthUser = await createUserWithEmailAndPassword(auth, email, password);

                // Atualizar perfil
                try {
                  await updateProfile(newAuthUser.user, {
                    displayName: userData.nome
                  });
                } catch (e) {
                  // Não foi possível definir displayName no Auth
                  // Erro ao atualizar displayName no Auth
                }

                // Atualizar documento no Firestore
                await updateDoc(doc(db, 'usuarios', pendingUser.id), {
                  uid: newAuthUser.user.uid,
                  precisaPrimeiroLogin: false,
                  status: 'active',
                  password: null, // Remover senha do Firestore
                  firstLoginAt: new Date()
                });

                return newAuthUser.user;
              } else {
                throw new Error('Senha incorreta');
              }
            } else {
              throw new Error('Email não encontrado');
            }
          } else {
            throw new Error('Email não encontrado');
          }
        } catch (pendingError) {
          throw new Error('Email ou senha incorretos');
        }
      }

      throw error;
    }
  };

  const register = async (email, password, profile = {}) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Atualizar perfil (displayName) se fornecido
      if (profile.nomeExibicao) {
        try {
          await updateProfile(result.user, {
            displayName: profile.nomeExibicao
          });
        } catch (e) {
          // Não foi possível definir displayName no Auth
          // Erro ao atualizar displayName no Auth
        }
      }

      // Salvar no Firestore
      try {
        const userDocRef = doc(db, 'usuarios', result.user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        const userData = {
          uid: result.user.uid,
          email: result.user.email,
          nome: profile.nomeExibicao || '',
          nomeExibicao: profile.nomeExibicao || '',
          photoURL: result.user.photoURL || null,
          emailVerified: result.user.emailVerified || false,
          role: 'user',
          status: 'active'
        };

        if (userDocSnap.exists()) {
          // Se o documento já existe, atualizar apenas os campos necessários
          await updateDoc(userDocRef, {
            ...userData,
            atualizadoEm: serverTimestamp()
          });
        } else {
          // Se não existe, criar novo documento
          await setDoc(userDocRef, {
            ...userData,
            criadoEm: serverTimestamp()
          });
          
          // Verificar se o documento foi criado corretamente
          const verifyDoc = await getDoc(userDocRef);
          if (!verifyDoc.exists()) {
            throw new Error('Erro: Documento do usuário não foi criado no Firestore');
          }
          
          // Usuário criado no Firestore com sucesso
        }
      } catch (e) {
        // Erro ao salvar usuário no Firestore
        // Se o erro for de permissão, relançar com mensagem específica
        if (e.code === 'permission-denied' || e.message?.includes('permission-denied')) {
          throw new Error('Permissão negada ao criar usuário. Verifique se as regras do Firestore foram deployadas corretamente.');
        }
        
        throw new Error('Erro ao salvar usuário no banco de dados: ' + (e.message || e.code || 'Erro desconhecido'));
      }

      return result.user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
